import type { FC } from "react";
import { logError, logUnhandled } from "../api/logError";
import { useDynamicFormState } from "./DynamicFormProvider";
import type { AnswerRequest, QuestionCard } from "./types";

/** Common inputs */
import { CountryMultiSelect } from "./inlineForm/inputs/CountryMultiSelect";
import { MeasureMultiSelect } from "./inlineForm/inputs/MeasureInput";
import { MultipleExternalLinkInput } from "./shared/MultipleExternalLinkInput";

/** Inline Inputs */
import { MultipleSubSupplierInputBase } from "./shared/MultipleSubSupplierInputBase";
import { Text as InlineText } from "./inlineForm/inputs/Text";
import { SingleSelect as InlineSingleSelect } from "./inlineForm/inputs/SingleSelect";
import { MultiSelect as InlineMultiSelect } from "./inlineForm/inputs/MultiSelect";
import { InlineDynamicMultiSelect } from "./inlineForm/inputs/InlineDynamicMultiSelect";
import { System as InlineSystem } from "./inlineForm/inputs/System";
import { Email as InlineEmail } from "./inlineForm/inputs/Email";
import { PhoneNumber as InlinePhoneNumber } from "./inlineForm/inputs/PhoneNumber";
import { MultiplePartnerInput as InlineMultiplePartnerInput } from "./inlineForm/inputs/MultiplePartnerInput";
import {
  PersonSingleAutocomplete as InlineSinglePerson,
  PersonMultipleAutocomplete as InlineMultiplePerson,
} from "./inlineForm/inputs/Person";
import { YesOrNo as InlineYesOrNo } from "./inlineForm/inputs/YesOrNo";
import { DateInput as InlineDateInput } from "./inlineForm/inputs/DateInput";
import { Dpia as InlineSingleDpiaInput } from "./inlineForm/inputs/Dpia";
import { TenantMulti as InlineMultiTenant } from "./inlineForm/inputs/TenantMulti";
import { ExternalLink as InlineExternalLink } from "./inlineForm/inputs/ExternalLink";
import { MultipleContactPersonInput as InlineMultipleContactPersonInput } from "./inlineForm/inputs/MultipleContactPersonInput";
import { Text as MultiStepText } from "./multiStepForm/inputs/Text";
import { CheckboxGroup as MultiStepCheckboxGroup } from "./multiStepForm/inputs/CheckboxGroup";
import { RadioGroup as MultiStepRadioGroup } from "./multiStepForm/inputs/RadioGroup";
import { System as MultiStepSystem } from "./multiStepForm/inputs/System";
import {
  PersonMultiSingleAutocomplete as MultiStepSinglePerson,
  PersonMultiMultipleAutocomplete as MultiStepMultiplePerson,
} from "./multiStepForm/inputs/Person";
import { YesOrNoRadioGroup as MultiStepYesOrNoRadioGroup } from "./multiStepForm/inputs/YesOrNoRadioGroup";
import { MultiStepDynamicMultiSelect } from "./multiStepForm/inputs/MultiStepDynamicMultiSelect";
import { DateInput as MultiStepDateInput } from "./multiStepForm/inputs/DateInput";
import { Dpia as MultiStepSingleDpiaInput } from "./multiStepForm/inputs/Dpia";
import { TenantMulti as MultiStepTenantMultiSelect } from "./multiStepForm/inputs/TenantMulti";
import { ExternalLink as MultiStepExternalLink } from "./multiStepForm/inputs/ExternalLink";
import { MultipleContactPersonInput as MultiStepMultipleContactPersonInput } from "./multiStepForm/inputs/MultipleContactPersonInput";
import { MultiplePartnerInput as MultiStepMultiplePartnerInput } from "./multiStepForm/inputs/MultiplePartnerInput";

export type InputProps<InputValue extends AnswerRequest["value"]> = {
  readonly: boolean;
  questionCard: QuestionCard;
  onSave: (value: InputValue | null) => Promise<unknown>;
};

type InputMap = Record<QuestionCard["input"]["type"], FC<any>>;

const inlineInputs = {
  multipleSubSupplierInput: MultipleSubSupplierInputBase,
  textInput: InlineText,
  singleChoiceInput: InlineSingleSelect,
  multipleChoiceInput: InlineMultiSelect,
  dynamicMultipleChoiceInput: InlineDynamicMultiSelect,
  systemInput: InlineSystem,
  emailInput: InlineEmail,
  phoneNumberInput: InlinePhoneNumber,
  personInput: InlineSinglePerson,
  yesOrNoInput: InlineYesOrNo,
  countryInput: CountryMultiSelect,
  multiplePersonInput: InlineMultiplePerson,
  dateInput: InlineDateInput,
  measureInput: MeasureMultiSelect,
  singleDpiaInput: InlineSingleDpiaInput,
  multipleTenantInput: InlineMultiTenant,
  multipleExternalLinkInput: MultipleExternalLinkInput,
  externalLinkInput: InlineExternalLink,
  multipleContactPersonInput: InlineMultipleContactPersonInput,
  multiplePartnerInput: InlineMultiplePartnerInput,
} satisfies InputMap;

const multiStepInputs = {
  multipleSubSupplierInput: () => {
    logError(
      new Error(
        "The multipleSubSupplierInput is not implemented for the multi-step form. If you see this error it is time to implement it."
      )
    );
    return null;
  },
  textInput: MultiStepText,
  singleChoiceInput: MultiStepRadioGroup,
  multipleChoiceInput: MultiStepCheckboxGroup,
  dynamicMultipleChoiceInput: MultiStepDynamicMultiSelect,
  systemInput: MultiStepSystem,
  emailInput: () => {
    throw new Error("The Email is not implemented for the multi-step form, see ExternalLink for guidance.");
  },
  phoneNumberInput: () => {
    throw new Error("The Phone Number is not implemented for the multi-step form, see ExternalLink for guidance.");
  },
  personInput: MultiStepSinglePerson,
  yesOrNoInput: MultiStepYesOrNoRadioGroup,
  countryInput: CountryMultiSelect,
  measureInput: MeasureMultiSelect,
  multiplePersonInput: MultiStepMultiplePerson,
  dateInput: MultiStepDateInput,
  singleDpiaInput: MultiStepSingleDpiaInput,
  multipleTenantInput: MultiStepTenantMultiSelect,
  multipleExternalLinkInput: MultipleExternalLinkInput,
  externalLinkInput: MultiStepExternalLink,
  multipleContactPersonInput: MultiStepMultipleContactPersonInput,
  multiplePartnerInput: MultiStepMultiplePartnerInput,
} satisfies InputMap;

type DynamicInputProps = {
  questionCard: QuestionCard;
  variant: "inline" | "multiStep";
};

export const DynamicInput: FC<DynamicInputProps> = ({ questionCard: rawQuestionCard, variant }) => {
  const formState = useDynamicFormState();
  const questionCard = formState.missingQuestionIds?.includes(rawQuestionCard.id)
    ? { ...rawQuestionCard, mandatory: true }
    : rawQuestionCard;

  const Inputs = variant === "inline" ? inlineInputs : multiStepInputs;

  const props: Pick<InputProps<AnswerRequest["value"]>, "questionCard" | "readonly"> = {
    questionCard,
    readonly: Boolean(formState.isReadOnly || questionCard.readonly),
  };

  switch (questionCard.input.type) {
    case "textInput":
      return (
        <Inputs.textInput
          {...props}
          numberOfRows={questionCard.input.numberOfRows}
          onSave={value =>
            formState.saveInput({ value, questionId: questionCard.id, type: "textInput" }, { rollbackOnError: false })
          }
        />
      );
    case "singleChoiceInput":
      return (
        <Inputs.singleChoiceInput
          {...props}
          options={questionCard.input.options}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "singleChoiceInput" })}
        />
      );
    case "multipleChoiceInput":
      return (
        <Inputs.multipleChoiceInput
          {...props}
          options={questionCard.input.options}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "multipleChoiceInput" })}
        />
      );
    case "dynamicMultipleChoiceInput":
      return (
        <Inputs.dynamicMultipleChoiceInput
          {...props}
          optionCategoryType={questionCard.input.categoryType}
          onSave={value =>
            formState.saveInput({ value, questionId: questionCard.id, type: "dynamicMultipleChoiceInput" })
          }
        />
      );
    case "countryInput":
      return (
        <Inputs.countryInput
          {...props}
          options={questionCard.input.options}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "countryInput" })}
          isInline={variant === "inline"}
        />
      );
    case "measureInput":
      return (
        <Inputs.measureInput
          {...props}
          options={questionCard.input.options}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "measureInput" })}
        />
      );
    case "systemInput":
      return (
        <Inputs.systemInput
          {...props}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "systemInput" })}
        />
      );
    case "emailInput":
      return (
        <Inputs.emailInput
          {...props}
          onSave={value =>
            formState.saveInput({ value, questionId: questionCard.id, type: "emailInput" }, { rollbackOnError: false })
          }
        />
      );
    case "phoneNumberInput":
      return (
        <Inputs.phoneNumberInput
          {...props}
          onSave={value =>
            formState.saveInput(
              { value, questionId: questionCard.id, type: "phoneNumberInput" },
              { rollbackOnError: false }
            )
          }
        />
      );
    case "personInput":
      return (
        <Inputs.personInput
          {...props}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "personInput" })}
        />
      );
    case "multiplePersonInput":
      return (
        <Inputs.multiplePersonInput
          {...props}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "multiplePersonInput" })}
        />
      );
    case "yesOrNoInput":
      return (
        <Inputs.yesOrNoInput
          {...props}
          yesDescription={questionCard.input.yesDescription}
          noDescription={questionCard.input.noDescription}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "yesOrNoInput" })}
        />
      );

    case "dateInput":
      return (
        <Inputs.dateInput
          {...props}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "dateInput" })}
        />
      );
    case "singleDpiaInput":
      return (
        <Inputs.singleDpiaInput
          {...props}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "singleDpiaInput" })}
        />
      );
    case "multipleTenantInput":
      return (
        <Inputs.multipleTenantInput
          {...props}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "multipleTenantInput" })}
        />
      );
    case "multipleExternalLinkInput":
      return (
        <Inputs.multipleExternalLinkInput
          {...props}
          onSave={value =>
            formState.saveInput({ value, questionId: questionCard.id, type: "multipleExternalLinkInput" })
          }
        />
      );
    case "multiplePartnerInput":
      return (
        <Inputs.multiplePartnerInput
          {...props}
          header={questionCard.input.header}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "multiplePartnerInput" })}
        />
      );
    case "externalLinkInput":
      return (
        <Inputs.externalLinkInput
          {...props}
          onSave={value => formState.saveInput({ value, questionId: questionCard.id, type: "externalLinkInput" })}
        />
      );
    case "multipleContactPersonInput":
      return (
        <Inputs.multipleContactPersonInput
          {...props}
          onSave={value =>
            formState.saveInput({ value, questionId: questionCard.id, type: "multipleContactPersonInput" })
          }
        />
      );
    case "multipleSubSupplierInput":
      return (
        <Inputs.multipleSubSupplierInput
          {...props}
          countryOptions={questionCard.input.countryOptions}
          onSave={value =>
            formState.saveInput({ value, questionId: questionCard.id, type: "multipleSubSupplierInput" })
          }
        />
      );
    default:
      return logUnhandled(questionCard.input, "input");
  }
};
